// src/pages/AddLesson.js
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createLesson, getMyCourses } from '../../../api/teacherApi';
import './AddLesson.css';

function AddLesson() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const preselectedCourse = queryParams.get('course');

  const [courses, setCourses] = useState([]);
  const [formData, setFormData] = useState({
    courseId: preselectedCourse || '',
    title: '',
    zoomLink: '',
    scheduledAt: '',
    durationMinutes: 60
  });
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      setPageLoading(true);
      const data = await getMyCourses();
      setCourses(data);
     
      // Eğer preselectedCourse varsa ve geçerliyse onu seç
      if (preselectedCourse && data.some(c => c._id === preselectedCourse)) {
        setFormData(prev => ({ ...prev, courseId: preselectedCourse }));
      } else if (data.length > 0 && !formData.courseId) {
        setFormData(prev => ({ ...prev, courseId: data[0]._id }));
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Kurslar yüklenirken hata oluştu: ' + (error.response?.data?.msg || error.message)
      });
    } finally {
      setPageLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Hem eski hem yeni sistem için uyumlu veri gönder
      await createLesson({
        courseId: formData.courseId,
        title: formData.title,
        zoomLink: formData.zoomLink,
        scheduledAt: formData.scheduledAt,
        durationMinutes: formData.durationMinutes,
        // Yeni sistem alanları da ekle
        content: {
          zoomLink: formData.zoomLink
        },
        duration: formData.durationMinutes
      });
      
      setMessage({
        type: 'success',
        text: 'Ders başarıyla eklendi! Yönlendiriliyorsunuz...'
      });
     
      setTimeout(() => {
        navigate('/teacher/lessons');
      }, 2000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.msg || error.response?.data?.message || 'Ders eklenirken hata oluştu.'
      });
    } finally {
      setLoading(false);
    }
  };

  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  const getSelectedCourse = () => {
    return courses.find(course => course._id === formData.courseId);
  };

  if (pageLoading) {
    return (
      <div className="add-lesson-loading">
        <div className="loading-spinner">📖</div>
        <p>Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="add-lesson">
      <div className="form-header">
        <h1>📖 Yeni Ders Ekle</h1>
        <p>Kursunuza yeni bir ders eklemek için aşağıdaki formu doldurun</p>
      </div>

      <form onSubmit={handleSubmit} className="lesson-form">
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="courseId">Kurs Seçin *</label>
            {courses.length === 0 ? (
              <div className="no-courses-warning">
                <p>Henüz kursunuz bulunmuyor. Önce bir kurs oluşturmalısınız.</p>
                <button
                  type="button"
                  onClick={() => navigate('/teacher/create-course')}
                  className="create-course-btn"
                >
                  Kurs Oluştur
                </button>
              </div>
            ) : (
              <select
                id="courseId"
                name="courseId"
                value={formData.courseId}
                onChange={handleChange}
                required
              >
                <option value="">Kurs seçin</option>
                {courses.map(course => (
                  <option key={course._id} value={course._id}>
                    {course.title}
                  </option>
                ))}
              </select>
            )}
          </div>

          {formData.courseId && (
            <>
              <div className="form-group">
                <label htmlFor="title">Ders Başlığı *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  placeholder="Örn: React Components ve Props"
                />
              </div>

              <div className="form-group">
                <label htmlFor="scheduledAt">Ders Tarihi ve Saati *</label>
                <input
                  type="datetime-local"
                  id="scheduledAt"
                  name="scheduledAt"
                  value={formData.scheduledAt}
                  onChange={handleChange}
                  required
                  min={getMinDateTime()}
                />
              </div>

              <div className="form-group">
                <label htmlFor="durationMinutes">Ders Süresi (dakika) *</label>
                <select
                  id="durationMinutes"
                  name="durationMinutes"
                  value={formData.durationMinutes}
                  onChange={handleChange}
                  required
                >
                  <option value="30">30 dakika</option>
                  <option value="45">45 dakika</option>
                  <option value="60">60 dakika</option>
                  <option value="90">90 dakika</option>
                  <option value="120">120 dakika</option>
                </select>
              </div>

              <div className="form-group full-width">
                <label htmlFor="zoomLink">Zoom Linki</label>
                <input
                  type="url"
                  id="zoomLink"
                  name="zoomLink"
                  value={formData.zoomLink}
                  onChange={handleChange}
                  placeholder="https://zoom.us/j/..."
                />
                <small>Boş bırakılırsa kursun varsayılan Zoom linki kullanılacak</small>
              </div>

              <div className="course-info">
                <h4>Kurs Bilgisi</h4>
                <p><strong>Kurs:</strong> {getSelectedCourse()?.title}</p>
                {getSelectedCourse()?.zoomLink && (
                  <p><strong>Varsayılan Zoom:</strong> {getSelectedCourse()?.zoomLink}</p>
                )}
              </div>
            </>
          )}
        </div>

        {formData.scheduledAt && (
          <div className="lesson-preview">
            <h3>Ders Önizleme</h3>
            <p><strong>Ders:</strong> {formData.title || 'Başlık girilmemiş'}</p>
            <p><strong>Ders Tarihi:</strong> {new Date(formData.scheduledAt).toLocaleString('tr-TR')}</p>
            <p><strong>Bitiş Tarihi:</strong> {new Date(new Date(formData.scheduledAt).getTime() + formData.durationMinutes * 60000).toLocaleString('tr-TR')}</p>
            <p><strong>Toplam Süre:</strong> {formData.durationMinutes} dakika</p>
          </div>
        )}

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        {formData.courseId && (
          <div className="form-actions">
            <button
              type="submit"
              disabled={loading}
              className="submit-btn"
            >
              {loading ? '⏳ Ekleniyor...' : '✅ Ders Ekle'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/teacher/lessons')}
              className="cancel-btn"
            >
              İptal
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

export default AddLesson;